import { create } from 'zustand';
import { SecurityRoleResponse, getSecurityRoles } from 'core/API/securityRoles';
import { HierarchyNode, getHierarchyNodes } from 'core/API/hierarchy';
import { isHiddenSecurityRole } from 'module/settings/settingsGameMasterTabs/GeneralSetting/constants';
import { fetchCurrentUserRoleFromMe } from 'core/utils/refreshCurrentUserRole';

interface UserDataState {
  // Security Roles
  securityRoles: SecurityRoleResponse[] | [];
  securityRolesLoading: boolean;
  securityRolesError: string | null;
  
  // User's Current Security Role
  currentUserRole: SecurityRoleResponse | null;
  
  // Hierarchy Nodes
  hierarchyNodes: HierarchyNode[];
  hierarchyNodesLoading: boolean;
  hierarchyNodesError: string | null;
  
  // Actions
  fetchSecurityRoles: () => Promise<void>;
  fetchHierarchyNodes: () => Promise<void>;
  setSecurityRoles: (roles: SecurityRoleResponse[]) => void;
  setHierarchyNodes: (nodes: HierarchyNode[]) => void;
  setCurrentUserRole: (role: SecurityRoleResponse) => void;
  refreshCurrentUserRole: () => Promise<SecurityRoleResponse | null>;
  clearUserData: () => void;
  initializeFromLocalStorage: () => void;
}

export const STORAGE_KEYS = {
  SECURITY_ROLES: 'BYOD-security-roles',
  HIERARCHY_NODES: 'BYOD-hierarchy-nodes'
};

const useUserDataStore = create<UserDataState>((set, _get) => ({
  // Initial state
  securityRoles: null,
  securityRolesLoading: false,
  securityRolesError: null,
  currentUserRole: null,
  hierarchyNodes: [],
  hierarchyNodesLoading: false,
  hierarchyNodesError: null,

  // Fetch security roles from API
  fetchSecurityRoles: async () => {
    set({ securityRolesLoading: true, securityRolesError: null });
    
    try {
      const response = await getSecurityRoles({ page: 1, itemsPerPage: 100 });
      
      // Filter out specific roles
      const filteredRoles = response.filter((role) => !isHiddenSecurityRole(role.name));
      
      // Store in Zustand
      set({ 
        securityRoles: filteredRoles, 
        securityRolesLoading: false,
        securityRolesError: null 
      });
      
      // Store in localStorage
      localStorage.setItem(STORAGE_KEYS.SECURITY_ROLES, JSON.stringify(filteredRoles));
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch security roles';
      set({ 
        securityRolesLoading: false, 
        securityRolesError: errorMessage 
      });
      console.error('Error fetching security roles:', error);
    }
  },

  // Fetch hierarchy nodes from API
  fetchHierarchyNodes: async () => {
    set({ hierarchyNodesLoading: true, hierarchyNodesError: null });
    
    try {
      const nodes = await getHierarchyNodes({ page: 1, itemsPerPage: 100 });
      
      // Store in Zustand
      set({ 
        hierarchyNodes: nodes, 
        hierarchyNodesLoading: false,
        hierarchyNodesError: null 
      });
      
      // Store in localStorage
      localStorage.setItem(STORAGE_KEYS.HIERARCHY_NODES, JSON.stringify(nodes));
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch hierarchy nodes';
      set({ 
        hierarchyNodesLoading: false, 
        hierarchyNodesError: errorMessage 
      });
      console.error('Error fetching hierarchy nodes:', error);
    }
  },

  // Set security roles directly (for manual updates)
  setSecurityRoles: (roles: SecurityRoleResponse[]) => {
    // Filter out specific roles
    const filteredRoles = roles.filter((role) => !isHiddenSecurityRole(role.name));
    
    set({ securityRoles: filteredRoles });
    localStorage.setItem(STORAGE_KEYS.SECURITY_ROLES, JSON.stringify(filteredRoles));
  },

  // Set current user's security role
  setCurrentUserRole: (role: SecurityRoleResponse) => {
    set({ currentUserRole: role });
    localStorage.setItem('BYOD-user-role', JSON.stringify(role));
  },

  refreshCurrentUserRole: async () => {
    try {
      const role = await fetchCurrentUserRoleFromMe();
      if (role) {
        set({ currentUserRole: role });
        localStorage.setItem('BYOD-user-role', JSON.stringify(role));
      }
      return role;
    } catch (error) {
      console.error('Failed to refresh current user role:', error);
      return null;
    }
  },

  // Set hierarchy nodes directly (for manual updates)
  setHierarchyNodes: (nodes: HierarchyNode[]) => {
    set({ hierarchyNodes: nodes });
    localStorage.setItem(STORAGE_KEYS.HIERARCHY_NODES, JSON.stringify(nodes));
  },

  // Clear all user data
  clearUserData: () => {
    set({
      securityRoles: null,
      securityRolesLoading: false,
      securityRolesError: null,
      hierarchyNodes: [],
      hierarchyNodesLoading: false,
      hierarchyNodesError: null,
    });
    
    // Clear from localStorage
    localStorage.removeItem(STORAGE_KEYS.SECURITY_ROLES);
    localStorage.removeItem(STORAGE_KEYS.HIERARCHY_NODES);
  },

  // Initialize data from localStorage
  initializeFromLocalStorage: () => {
    try {
      // Load security roles from localStorage
      const storedSecurityRoles = localStorage.getItem(STORAGE_KEYS.SECURITY_ROLES);
      if (storedSecurityRoles) {
        const securityRoles = JSON.parse(storedSecurityRoles);
        set({ securityRoles });
      }

      // Load current user role from localStorage
      const storedUserRole = localStorage.getItem('BYOD-user-role');
      if (storedUserRole) {
        const currentUserRole = JSON.parse(storedUserRole);
        set({ currentUserRole });
      }

      // Load hierarchy nodes from localStorage
      const storedHierarchyNodes = localStorage.getItem(STORAGE_KEYS.HIERARCHY_NODES);
      if (storedHierarchyNodes) {
        const hierarchyNodes = JSON.parse(storedHierarchyNodes);
        set({ hierarchyNodes });
      }
      
    } catch (error) {
      console.error('Error initializing user data from localStorage:', error);
    }
  },
}));

export default useUserDataStore;
